// A1 : création d'une installation (G07, charte D-038) en quatre étapes tenues en état local (identité,
// localisation, exploitation, vérification) et un seul envoi final. L'installation est créée à l'état
// brouillon ; le rattachement territorial et la recherche de doublons sont faits par le serveur.

import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CATEGORIES_INSTALLATIONS, NIVEAUX_CONFIDENTIALITE } from '@carburanttrack/shared';
import { api } from '../../api/client';
import type { Organisation } from '../../api/types';
import { aUnRole, useSession } from '../../app/session';
import { CarteProvinces } from '../../composants/CarteProvinces';
import { Message, MessageErreur } from '../../composants/Etats';
import { libelleCategorie, libelleConfidentialite, libelleProvince } from '../../composants/format';
import { useCarte } from '../../api/requetes';
import { useDoublons } from '../accueil/communs';

interface Rattachement {
  provinceCode: string | null;
  departementId: string | null;
  localiteNom: string | null;
  distanceLocaliteMetres: number | null;
  motif: string;
}

const ETAPES = ['Identité', 'Localisation', 'Exploitation', 'Vérification'] as const;
const TITRES = ['1. Identité de l’installation', '2. Localisation', '3. Exploitation et diffusion', '4. Vérification avant création'];

const AIDES_CONFIDENTIALITE: Record<string, string> = {
  publique: 'Lisible par tout rôle autorisé à consulter les installations.',
  restreinte: 'Valeur proposée par défaut pour le référentiel.',
  confidentielle: 'Lisible seulement par les rôles habilités au niveau confidentiel.',
  strategique: 'Rôles habilités au niveau stratégique ; chaque consultation de la fiche est journalisée.',
};

export function NouvelleInstallation() {
  const { etat } = useSession();
  const client = useQueryClient();
  const naviguer = useNavigate();
  const carte = useCarte();
  const doublons = useDoublons();
  const [etape, setEtape] = useState(1);
  const [nom, setNom] = useState('');
  const [categorie, setCategorie] = useState('station');
  const [longitude, setLongitude] = useState('');
  const [latitude, setLatitude] = useState('');
  const [adresse, setAdresse] = useState('');
  const [horaires, setHoraires] = useState('');
  const [confidentialite, setConfidentialite] = useState('restreinte');
  const [organisationId, setOrganisationId] = useState('');
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);
  const [position, setPosition] = useState<string | null>(null);
  const choixOrganisation = aUnRole(etat, 'administrateur_national', 'agent_administratif');
  const organisations = useQuery({
    queryKey: ['organisations', 'toutes'],
    queryFn: async () => (await api<Organisation[]>('/api/v1/organisations?limite=200')).donnees,
    enabled: choixOrganisation,
  });
  const lon = Number(longitude.replace(',', '.'));
  const lat = Number(latitude.replace(',', '.'));
  const coordonneesSaisies = longitude !== '' && latitude !== '' && Number.isFinite(lon) && Number.isFinite(lat);
  const apercu = useQuery({
    queryKey: ['rattachement', lon, lat],
    queryFn: async () => (await api<Rattachement>(`/api/v1/rattachement?lon=${lon}&lat=${lat}`)).donnees,
    enabled: coordonneesSaisies,
  });
  const nomOrganisation = organisationId ? organisations.data?.find((o) => o.id === organisationId)?.nom : null;
  const geolocalisation = typeof navigator !== 'undefined' && 'geolocation' in navigator;
  const seuil = doublons.data?.meta.seuilMetres;

  const manquants: string[] = [];
  if (!nom.trim()) manquants.push('le nom');
  if (!coordonneesSaisies) manquants.push('les coordonnées');
  if (coordonneesSaisies && apercu.data && apercu.data.provinceCode === null) manquants.push('des coordonnées situées dans une province du Gabon');

  function utiliserPosition() {
    setPosition('Recherche de la position de l’appareil en cours.');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLatitude(p.coords.latitude.toFixed(6));
        setLongitude(p.coords.longitude.toFixed(6));
        setPosition(`Position de l’appareil reprise, précision d’environ ${Math.round(p.coords.accuracy)} m.`);
      },
      () => setPosition('Position de l’appareil indisponible : autorisation refusée ou signal absent. Saisissez les coordonnées.'),
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  }

  async function creer(e: FormEvent) {
    e.preventDefault();
    // La touche Entrée avant la dernière étape fait seulement avancer : un seul envoi, à la fin.
    if (etape < 4) {
      setEtape(etape + 1);
      return;
    }
    if (manquants.length > 0) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await api<{ id: string; doublonsPotentiels: unknown[] }>('/api/v1/installations', {
        corps: {
          nom,
          categorie,
          longitude: lon,
          latitude: lat,
          adresse: adresse || null,
          horaires: horaires || null,
          confidentialite,
          ...(choixOrganisation && organisationId ? { organisationId } : {}),
        },
      });
      await client.invalidateQueries({ queryKey: ['installations'] });
      await client.invalidateQueries({ queryKey: ['accueil-situation'] });
      await client.invalidateQueries({ queryKey: ['doublons'] });
      naviguer(`/referentiel/installations/${r.donnees.id}`, { state: { creee: true, doublons: r.donnees.doublonsPotentiels.length } });
    } catch (err) {
      setErreur(err);
    } finally {
      setEnvoi(false);
    }
  }

  const recapitulatif: [string, string | null][] = [
    ['Nom', nom.trim() || null],
    ['Catégorie', libelleCategorie(categorie)],
    ['Organisation', choixOrganisation ? (nomOrganisation ?? 'Mon organisation') : 'Mon organisation'],
    ['Province', apercu.data?.provinceCode ? libelleProvince(apercu.data.provinceCode) : null],
    ['Coordonnées', coordonneesSaisies ? `${latitude} ; ${longitude}` : null],
    ['Adresse', adresse.trim() || null],
    ['Horaires', horaires.trim() || null],
    ['Diffusion', libelleConfidentialite(confidentialite)],
  ];

  return (
    <div className="page-nouvelle-installation">
      <div className="page-tete">
        <div>
          <span className="contexte">Référentiel des installations</span>
          <h1>Nouvelle installation</h1>
          <p>L’installation est créée à l’état brouillon, puis soumise à la validation de l’administrateur national.</p>
        </div>
      </div>

      <ol className="etapes" aria-label="Étapes">
        {ETAPES.map((libelle, i) => {
          const n = i + 1;
          return (
            <li key={libelle}>
              <button type="button" aria-current={n === etape ? 'step' : undefined} className={n < etape ? 'faite' : undefined} onClick={() => setEtape(n)}>
                <span className="numero" aria-hidden="true">
                  {n}
                </span>
                {libelle}
                {n < etape && <span className="visuellement-cache"> (parcourue)</span>}
              </button>
            </li>
          );
        })}
      </ol>

      <div className="deux-colonnes">
        <form className="principal carte-bloc etape-formulaire" aria-labelledby="titre-etape" onSubmit={creer}>
          <h2 id="titre-etape">{TITRES[etape - 1]}</h2>

          {etape === 1 && (
            <div className="formulaire-ligne">
              <div className="champ pleine-ligne">
                <label htmlFor="n-nom">Nom de l’installation</label>
                <input id="n-nom" placeholder="Par exemple : Station Akanda Nord" value={nom} onChange={(e) => setNom(e.target.value)} />
              </div>
              <div className="champ">
                <label htmlFor="n-categorie">Catégorie</label>
                <select id="n-categorie" value={categorie} onChange={(e) => setCategorie(e.target.value)}>
                  {CATEGORIES_INSTALLATIONS.map((c) => (
                    <option key={c} value={c}>
                      {libelleCategorie(c)}
                    </option>
                  ))}
                </select>
              </div>
              {choixOrganisation && (
                <div className="champ">
                  <label htmlFor="n-organisation">Organisation responsable</label>
                  <select id="n-organisation" value={organisationId} onChange={(e) => setOrganisationId(e.target.value)}>
                    <option value="">Mon organisation</option>
                    {organisations.data?.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.nom}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {etape === 2 && (
            <div className="etape-localisation">
              <div>
                <div className="formulaire-ligne">
                  <div className="champ">
                    <label htmlFor="n-lat">Latitude</label>
                    <input id="n-lat" inputMode="decimal" placeholder="0,4162" value={latitude} onChange={(e) => setLatitude(e.target.value)} />
                  </div>
                  <div className="champ">
                    <label htmlFor="n-lon">Longitude</label>
                    <input id="n-lon" inputMode="decimal" placeholder="9,4673" value={longitude} onChange={(e) => setLongitude(e.target.value)} />
                  </div>
                </div>
                <p className="aide-champ">Degrés décimaux. La province, le département et la localité la plus proche sont rattachés automatiquement.</p>
                {geolocalisation && (
                  <button type="button" className="bouton" onClick={utiliserPosition}>
                    Utiliser la position de cet appareil
                  </button>
                )}
                {position && <p className="attenue" role="status">{position}</p>}
                <div className="champ espace-haut">
                  <label htmlFor="n-adresse">Adresse</label>
                  <input id="n-adresse" placeholder="Quartier, voie, repère" value={adresse} onChange={(e) => setAdresse(e.target.value)} />
                </div>
              </div>
              <figure className="apercu-rattachement">
                {carte.data && (
                  <CarteProvinces
                    carte={carte.data}
                    points={coordonneesSaisies ? [{ id: 'nouvelle', nom: nom || 'Nouvelle installation', longitude: lon, latitude: lat, categorie }] : []}
                    pointMisEnAvant="nouvelle"
                    selection={apercu.data?.provinceCode ?? null}
                    titre="Aperçu de la localisation sur la carte des provinces"
                    legende={false}
                  />
                )}
                <figcaption>
                  {!coordonneesSaisies && 'Saisissez les coordonnées pour voir le rattachement.'}
                  {coordonneesSaisies && apercu.data && (
                    <>
                      <b>{apercu.data.provinceCode ? `${libelleProvince(apercu.data.provinceCode)}${apercu.data.localiteNom ? `, près de ${apercu.data.localiteNom}` : ''}` : 'Hors des provinces du Gabon'}</b>
                      <span>{apercu.data.motif}</span>
                    </>
                  )}
                </figcaption>
              </figure>
            </div>
          )}

          {etape === 3 && (
            <div className="formulaire-ligne">
              <div className="champ">
                <label htmlFor="n-horaires">Horaires</label>
                <input id="n-horaires" placeholder="Tous les jours, 6 h à 22 h" value={horaires} onChange={(e) => setHoraires(e.target.value)} />
              </div>
              <fieldset className="champ choix-confidentialite">
                <legend>Confidentialité</legend>
                {NIVEAUX_CONFIDENTIALITE.filter((n) => n !== 'personnelle').map((n) => (
                  <label key={n} className={confidentialite === n ? 'carte-radio choisie' : 'carte-radio'}>
                    <input type="radio" name="confidentialite" value={n} checked={confidentialite === n} onChange={() => setConfidentialite(n)} />
                    <span>
                      <b>{libelleConfidentialite(n)}</b>
                      <small>{AIDES_CONFIDENTIALITE[n]}</small>
                    </span>
                  </label>
                ))}
              </fieldset>
            </div>
          )}

          {etape === 4 && (
            <div className="etape-verification">
              <p className="note">
                À la création, CarburantTrack recherche les doublons (nom proche et moins de {seuil !== undefined ? `${String(seuil)} m` : 'la distance paramétrée'}). Une paire probable apparaîtra dans « Doublons à traiter » avant la validation.
              </p>
              {manquants.length > 0 && (
                <p className="message avertissement" role="alert">
                  À compléter avant création : {manquants.join(' et ')}.
                </p>
              )}
              {manquants.length === 0 && <Message type="info">Tout est prêt : l’installation sera créée à l’état brouillon.</Message>}
              {erreur !== null && <MessageErreur erreur={erreur} titre="Création refusée" />}
            </div>
          )}

          <div className="navigation-etapes">
            {etape > 1 && (
              <button type="button" className="bouton" onClick={() => setEtape(etape - 1)}>
                Étape précédente
              </button>
            )}
            {etape < 4 && (
              <button type="button" className="bouton primaire" onClick={() => setEtape(etape + 1)}>
                Continuer
              </button>
            )}
            {etape === 4 && (
              <button type="submit" className="bouton primaire" disabled={envoi || manquants.length > 0}>
                {envoi ? 'Création en cours' : 'Créer à l’état brouillon'}
              </button>
            )}
            <Link className="bouton discret pousse-droite" to="/referentiel/installations">
              Annuler
            </Link>
          </div>
        </form>

        <aside className="lateral" aria-labelledby="titre-recapitulatif">
          <section className="carte-bloc">
            <h2 id="titre-recapitulatif" className="sur-titre">
              Récapitulatif
            </h2>
            <dl className="paires">
              {recapitulatif.map(([cle, valeur]) => (
                <div key={cle}>
                  <dt>{cle}</dt>
                  <dd>{valeur ?? <span className="indisponible">à renseigner</span>}</dd>
                </div>
              ))}
            </dl>
            <p className="attenue espace-haut">Statut à la création : brouillon, jusqu’à sa soumission puis sa validation.</p>
          </section>
        </aside>
      </div>
    </div>
  );
}
